/**
 * RoleMessageTabs - Tabbed display for all 5 roles' messages
 */

import { useState } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

interface RoleMessage {
  role: string;
  messages: Array<{
    source: string;
    text: string;
    icon?: string;
  }>;
}

interface RoleMessageTabsProps {
  roleMessages: RoleMessage[];
}

const roleColors: Record<string, string> = {
  BIOLOGIST: 'text-[#367A25]',
  DIVER: 'text-[#12356B]',
  FIXER: 'text-[#c4562a]',
  HISTORIAN: 'text-[#12356B]',
  HUNTER: 'text-[#c4562a]',
};

const roleIcons: Record<string, string> = {
  BIOLOGIST: '/icons/biologist.png',
  DIVER: '/icons/diver.png',
  FIXER: '/icons/fixer.png',
  HISTORIAN: '/icons/historian.png',
  HUNTER: '/icons/hunter.png',
};

const sourceIcons: Record<string, string> = {
  MOTHERSHIP: '/icons/MOTHERSHIP.png',
  GEE: '/icons/GEE.png',
  DOC: '/icons/doc.png',
  BIOLOGIST: '/icons/biologist.png',
  DIVER: '/icons/diver.png',
  FIXER: '/icons/fixer.png',
  HISTORIAN: '/icons/historian.png',
  HUNTER: '/icons/hunter.png',
};

export function RoleMessageTabs({ roleMessages }: RoleMessageTabsProps) {
  const [activeRole, setActiveRole] = useState(roleMessages[0]?.role || '');
  const roleTabClass =
    'flex-1 min-w-[100px] flex items-center gap-2 h-10 rounded-lg bg-transparent text-slate-600 data-[state=active]:bg-white data-[state=active]:text-[#12356B] data-[state=active]:shadow-sm hover:text-[#12356B] transition-colors';

  return (
    <Tabs value={activeRole} onValueChange={setActiveRole}>
      <TabsList className="w-full flex-wrap h-auto bg-[#E2E5EC] p-1 rounded-xl">
        {roleMessages.map((rm) => {
          const iconPath = roleIcons[rm.role];
          return (
            <TabsTrigger key={rm.role} value={rm.role} className={roleTabClass}>
              {iconPath && <img src={iconPath} alt={rm.role} className="w-4 h-4 object-contain" />}
              <span className={roleColors[rm.role] || 'text-slate-700'}>{rm.role}</span>
            </TabsTrigger>
          );
        })}
      </TabsList>
      {roleMessages.map((rm) => (
        <TabsContent key={rm.role} value={rm.role} className="mt-4 space-y-3">
          {rm.messages.map((msg, idx) => {
            const iconPath = sourceIcons[msg.source];
            return (
              <div key={idx} className="bg-[#E2E5EC]/50 border border-[#CED4DE] rounded-lg p-4">
                <div className="flex items-start gap-3">
                  {iconPath && (
                    <img src={iconPath} alt={msg.source} className="w-8 h-8 object-contain flex-shrink-0 mt-1" />
                  )}
                  <div className="flex-1">
                    <div className="text-xs font-semibold text-slate-600 mb-1">{msg.source}</div>
                    <div className="text-sm text-slate-800 leading-relaxed">
                      {msg.text.split('\n').map((line, i) => (
                        <p key={i} className={i > 0 ? 'mt-2' : ''}>
                          {line}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </TabsContent>
      ))}
    </Tabs>
  );
}
